import type { ReactNode } from 'react';
import { Button } from './ui/button';

export interface LiffLoginGateProps {
  /** LIFF 是否已初始化完成 */
  isReady: boolean;
  /** 是否應使用 LIFF（含 Mock） */
  shouldUseLiff: boolean;
  /** 是否已登入 LINE */
  isLoggedIn: boolean;
  /** LIFF 或 ensure 錯誤 */
  error?: Error | null;
  /** 點擊「使用 LINE 登入」時呼叫（由父層傳入 liff.login） */
  onLoginClick: () => void;
  /** 通過時渲染的內容 */
  children?: ReactNode;
}

/**
 * 共用「未登入／登入失敗」UI（Member / Lite / Survey / Pickup 共用此元件）。
 * 不另做載入畫面：LIFF 初始化時 LINE 自有載入；未就緒時直接渲染 children，由子層依 isReady 再決定是否取 token。
 * 僅在「已就緒且未登入」或「有錯誤」時顯示 gate。
 */
export function LiffLoginGate({
  isReady,
  shouldUseLiff,
  isLoggedIn,
  error,
  onLoginClick,
  children,
}: LiffLoginGateProps) {
  // 需 LIFF 且已就緒但未登入：顯示「需要 LINE 登入」與重試按鈕
  if (shouldUseLiff && isReady && !isLoggedIn) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 max-w-md w-full text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            需要 LINE 登入
          </h2>
          <p className="text-gray-600 mb-4">
            若登入失敗或已關閉登入頁，請點擊下方按鈕重試。
          </p>
          {error && (
            <p className="text-sm text-red-600 mb-4">{error.message}</p>
          )}
          <Button onClick={onLoginClick} className="w-full">
            使用 LINE 登入
          </Button>
        </div>
      </div>
    );
  }

  // 有錯誤時：顯示錯誤與可選重新載入／重試
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 max-w-md w-full text-center">
          <div className="text-red-600 mb-4">
            <svg
              className="mx-auto h-12 w-12"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">發生錯誤</h2>
          <p className="text-gray-600 mb-4">{error.message}</p>
          <div className="flex flex-col gap-2">
            <Button onClick={() => window.location.reload()}>重新載入</Button>
            {shouldUseLiff && (
              <Button variant="outline" onClick={onLoginClick}>
                重試登入
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
